import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Badge, ListGroup, Spinner, Alert, Form } from 'react-bootstrap';
import { ExternalLink } from 'lucide-react';
import { useComponentRelatedItems, ComponentMaintenanceActivity } from '../../hooks/components/useComponentRelatedItems';
import { WorkOrder, WorkOrderStatus, WorkOrderPriority } from '../../types/workOrders';
import { Issue, IssuePriority, IssueWithBuilding } from '../../types/issues';
import { ProjectStatus } from '../../types/projects';
import { BuildingCheckWithTemplate, checkDisplayName } from '../BuildingChecks/buildingChecksUtils';
import WorkOrderDetailModal from '../Förvaltning/components/WorkOrderDetailModal';
import IssueDetailModal from '../Förvaltning/components/IssueDetailModal';
import CreateWorkOrderModal from '../Förvaltning/components/CreateWorkOrderModal';
import { issuesApi } from '../../api/issuesApi';
import { useUsersData, Userprofile } from '../../Helpers/UsersContext';

interface ComponentRelatedItemsProps {
    buildingKey: string;
    componentKey: string;
    buildingName?: string;
}

// Status badge styles
const workOrderStatusBadge: Record<WorkOrderStatus, { bg: string; label: string }> = {
    [WorkOrderStatus.OPEN]: { bg: 'primary', label: 'Öppen' },
    [WorkOrderStatus.COMPLETED]: { bg: 'success', label: 'Klar' },
    [WorkOrderStatus.CANCELLED]: { bg: 'secondary', label: 'Arkiverad utan åtgärd' },
};

const issuePriorityBadge: Record<IssuePriority, { bg: string; label: string }> = {
    [IssuePriority.HIGH]: { bg: 'danger', label: 'Hög' },
    [IssuePriority.MEDIUM]: { bg: 'warning', label: 'Medium' },
    [IssuePriority.LOW]: { bg: 'info', label: 'Låg' },
};

const projectStatusBadge: Record<ProjectStatus, { bg: string; label: string }> = {
    [ProjectStatus.PLANNED]: { bg: 'secondary', label: 'Planerad' },
    [ProjectStatus.IN_PROGRESS]: { bg: 'primary', label: 'Pågående' },
    [ProjectStatus.COMPLETED]: { bg: 'success', label: 'Utförd' },
};

// Format date for display (YYYY-MM-DD from storage format)
const formatDateDisplay = (dateString: string | undefined): string => {
    if (!dateString) return '–';
    // Storage format is "YYYY-MM-DD hh:mm:ss" or "YYYY-MM-DD"
    return dateString.substring(0, 10);
};

// Format cost for display
const formatCost = (cost: number | undefined): string => {
    if (cost === undefined || cost === null) return '';
    return cost.toLocaleString('sv-SE') + ' kr';
};

const ComponentRelatedItems: React.FC<ComponentRelatedItemsProps> = ({
    buildingKey,
    componentKey,
    buildingName = 'Okänd byggnad',
}) => {
    const {
        workOrders,
        issues,
        maintenanceActivities,
        inspections,
        projects,
        archivedWorkOrders,
        archivedIssues,
        isLoading,
        isLoadingArchived,
        error,
        showArchived,
        setShowArchived,
        refetch,
        hasAnyItems,
        hasArchivedItems,
    } = useComponentRelatedItems(buildingKey, componentKey);

    const { users } = useUsersData();

    // Synchronous user name lookup for modal
    const getUserName = useCallback((uid: string): string => {
        if (!users) return uid;
        const user = users.find((u: Userprofile) => u.key === uid);
        return user?.name || uid;
    }, [users]);

    // Modal state
    const [selectedWorkOrderKey, setSelectedWorkOrderKey] = useState<string | null>(null);
    const [selectedIssue, setSelectedIssue] = useState<IssueWithBuilding | null>(null);
    // Same flow as Förvaltning: the detail modal hands over the live link and
    // priority, and the work order form converts through issueApi.
    const [convertingIssue, setConvertingIssue] = useState<{ issue: IssueWithBuilding; componentLabel?: string } | null>(null);

    // Don't render if loading or no items
    if (isLoading) {
        return (
            <div style={{ marginTop: '30px' }}>
                <div className="d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" />
                    <span>Laddar relaterade aktiviteter...</span>
                </div>
            </div>
        );
    }

    // Check if we have any items (including archived if toggle is on)
    const hasVisibleItems = hasAnyItems || (showArchived && hasArchivedItems);

    if (!hasVisibleItems && !hasArchivedItems) {
        return null; // Nothing to show at all
    }

    // If we only have archived items and toggle is off, show hint
    if (!hasAnyItems && hasArchivedItems && !showArchived) {
        return (
            <div style={{ marginTop: '30px' }}>
                <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '15px' }}>
                    <h4 style={{ margin: 0 }}>Relaterade aktiviteter</h4>
                    <Form.Check
                        type="switch"
                        id="show-archived-switch"
                        label="Visa arkiverade"
                        checked={showArchived}
                        onChange={(e) => setShowArchived(e.target.checked)}
                    />
                </div>
                <p style={{ color: '#666', fontStyle: 'italic' }}>
                    Inga aktiva aktiviteter. Det finns arkiverade aktiviteter att visa.
                </p>
            </div>
        );
    }

    // Combine active and archived items if showing archived
    const displayWorkOrders = showArchived ? [...workOrders, ...archivedWorkOrders] : workOrders;
    const displayIssues = showArchived ? [...issues, ...archivedIssues] : issues;

    // Handle work order click
    const handleWorkOrderClick = (wo: WorkOrder) => {
        setSelectedWorkOrderKey(wo.key);
    };

    // Handle issue click
    const handleIssueClick = (issue: Issue) => {
        // Convert Issue to IssueWithBuilding for the modal
        const issueWithBuilding: IssueWithBuilding = {
            ...issue,
            buildingName,
        };
        setSelectedIssue(issueWithBuilding);
    };


    // Handle modal callbacks
    const handleWorkOrderCompleted = () => {
        setSelectedWorkOrderKey(null);
        refetch();
    };

    const handleWorkOrderCancelled = () => {
        setSelectedWorkOrderKey(null);
        // No refetch on cancel - nothing changed
    };

    const handleIssueClose = () => {
        setSelectedIssue(null);
    };

    const handleIssueConvert = (latest: { componentKey?: string; componentCategory?: IssueWithBuilding['componentCategory']; priority: IssuePriority; componentLabel?: string }) => {
        if (!selectedIssue) return;
        setConvertingIssue({
            issue: { ...selectedIssue, componentKey: latest.componentKey, componentCategory: latest.componentCategory, priority: latest.priority },
            componentLabel: latest.componentLabel,
        });
        setSelectedIssue(null);
    };

    const handleIssueArchive = async (reason?: string) => {
        if (!selectedIssue) return;
        await issuesApi.archiveDirectly(selectedIssue.key, reason);
        setSelectedIssue(null);
        refetch();
    };

    return (
        <div style={{ marginTop: '30px' }}>
            {/* Section Header */}
            <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '15px' }}>
                <h4 style={{ margin: 0 }}>Relaterade aktiviteter</h4>
                <div className="d-flex align-items-center gap-2">
                    {isLoadingArchived && <Spinner animation="border" size="sm" />}
                    <Form.Check
                        type="switch"
                        id="show-archived-switch"
                        label={showArchived ? 'Dölj arkiverade' : 'Visa arkiverade'}
                        checked={showArchived}
                        onChange={(e) => setShowArchived(e.target.checked)}
                    />
                </div>
            </div>

            {/* Error state */}
            {error && (
                <Alert variant="warning" dismissible onClose={() => refetch()}>
                    {error}
                </Alert>
            )}

            {/* Work Orders */}
            {displayWorkOrders.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                    <h6 style={{ marginBottom: '10px', fontWeight: 500, color: '#555' }}>
                        Ärende ({displayWorkOrders.length})
                    </h6>
                    <ListGroup>
                        {displayWorkOrders.map((wo) => {
                            const status = workOrderStatusBadge[wo.status];
                            const isArchived = wo.status !== WorkOrderStatus.OPEN;
                            // Show completion date for completed, due date for open
                            const dateLabel = wo.status === WorkOrderStatus.COMPLETED && wo.completedAt
                                ? `Slutförd: ${formatDateDisplay(wo.completedAt)}`
                                : wo.dueDate
                                    ? `Förfallodatum: ${formatDateDisplay(wo.dueDate)}`
                                    : null;
                            return (
                                <ListGroup.Item
                                    key={wo.key}
                                    action
                                    onClick={() => handleWorkOrderClick(wo)}
                                    className="d-flex justify-content-between align-items-center"
                                    style={{ cursor: 'pointer', opacity: isArchived ? 0.7 : 1 }}
                                >
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {wo.title}
                                        </div>
                                        {dateLabel && (
                                            <small style={{ color: '#666' }}>
                                                {dateLabel}
                                            </small>
                                        )}
                                    </div>
                                    <Badge bg={status.bg}>{status.label}</Badge>
                                </ListGroup.Item>
                            );
                        })}
                    </ListGroup>
                </div>
            )}

            {/* Issues */}
            {displayIssues.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                    <h6 style={{ marginBottom: '10px', fontWeight: 500, color: '#555' }}>
                        Felanmälningar ({displayIssues.length})
                    </h6>
                    <ListGroup>
                        {displayIssues.map((issue) => {
                            const priority = issuePriorityBadge[issue.priority];
                            return (
                                <ListGroup.Item
                                    key={issue.key}
                                    action
                                    onClick={() => handleIssueClick(issue)}
                                    className="d-flex justify-content-between align-items-center"
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {issue.description}
                                        </div>
                                        <small style={{ color: '#666' }}>
                                            {formatDateDisplay(issue.createdAt)}
                                        </small>
                                    </div>
                                    <Badge bg={priority.bg}>{priority.label}</Badge>
                                </ListGroup.Item>
                            );
                        })}
                    </ListGroup>
                </div>
            )}

            {/* Maintenance Activities */}
            {maintenanceActivities.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                    <h6 style={{ marginBottom: '10px', fontWeight: 500, color: '#555' }}>
                        Underhållsaktiviteter ({maintenanceActivities.length})
                    </h6>
                    <ListGroup>
                        {maintenanceActivities.map((activity) => (
                            <ListGroup.Item
                                key={activity.templateId}
                                className="d-flex justify-content-between align-items-center"
                            >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {activity.templateName}
                                    </div>
                                    <small style={{ color: '#666' }}>
                                        Nästa: {formatDateDisplay(activity.nextDate)}
                                        {activity.cost !== undefined && ` • ${formatCost(activity.cost)}`}
                                    </small>
                                </div>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </div>
            )}

            {/* Inspections */}
            {inspections.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                    <h6 style={{ marginBottom: '10px', fontWeight: 500, color: '#555' }}>
                        Kontroller ({inspections.length})
                    </h6>
                    <ListGroup>
                        {inspections.map((check) => (
                            <ListGroup.Item
                                key={check.checkKey}
                                action
                                as={Link}
                                to={`/building/${buildingKey}/checks`}
                                target="_blank"
                                className="d-flex justify-content-between align-items-center"
                                style={{ cursor: 'pointer' }}
                            >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {checkDisplayName(check, check.template?.name || check.templateId)}
                                    </div>
                                    <small style={{ color: '#666' }}>
                                        Nästa: {formatDateDisplay(check.nextDueDate)}
                                    </small>
                                </div>
                                <ExternalLink size={16} style={{ color: '#666' }} />
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </div>
            )}

            {/* Projects */}
            {projects.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                    <h6 style={{ marginBottom: '10px', fontWeight: 500, color: '#555' }}>
                        Projekt ({projects.length})
                    </h6>
                    <ListGroup>
                        {projects.map((project) => {
                            const status = projectStatusBadge[project.status];
                            return (
                                <ListGroup.Item
                                    key={project.key}
                                    action
                                    as={Link}
                                    to={`/building/${project.buildingKey}/projects/${project.key}`}
                                    target="_blank"
                                    className="d-flex justify-content-between align-items-center"
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {project.title}
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <Badge bg={status.bg}>{status.label}</Badge>
                                        <ExternalLink size={16} style={{ color: '#666' }} />
                                    </div>
                                </ListGroup.Item>
                            );
                        })}
                    </ListGroup>
                </div>
            )}

            {/* Work Order Detail Modal */}
            {selectedWorkOrderKey && (
                <WorkOrderDetailModal
                    show={!!selectedWorkOrderKey}
                    onHide={() => setSelectedWorkOrderKey(null)}
                    workOrderKey={selectedWorkOrderKey}
                    onCompleted={handleWorkOrderCompleted}
                    onCancelled={handleWorkOrderCancelled}
                    onDeleted={() => {
                        setSelectedWorkOrderKey(null);
                        refetch();
                    }}
                    getUserName={getUserName}
                />
            )}

            {/* Issue Detail Modal */}
            {selectedIssue && (
                <IssueDetailModal
                    show={true}
                    onHide={handleIssueClose}
                    issue={selectedIssue}
                    onConvert={handleIssueConvert}
                    onArchive={handleIssueArchive}
                    onIssueChanged={refetch}
                />
            )}

            {convertingIssue && (
                <CreateWorkOrderModal
                    show={true}
                    onHide={() => setConvertingIssue(null)}
                    issue={convertingIssue.issue}
                    issueComponentLabel={convertingIssue.componentLabel}
                    onSuccess={() => {
                        setConvertingIssue(null);
                        refetch();
                    }}
                />
            )}
        </div>
    );
};

export default ComponentRelatedItems;
